import { Injectable } from "@angular/core";
import { QPilotPaymentMethodType } from "../../models/qpilot/payment-method.interface";

/**
 * Gateway payment type for Stripe and PayPal
 * These are specific payment types within those gateways
 */
export interface GatewayPaymentType {
  code: string;
  name: string;
}

/**
 * Payment integration configuration
 * Defines fields and settings for each gateway
 */
export interface PaymentIntegrationConfig {
  ApiAccount?: string | null;
  ApiKey1?: string | null;
  ApiKey2?: string | null;
  ApiKey3?: string | null;
  GatewayPaymentType?: GatewayPaymentType[];
  TestMode: boolean;
  AuthorizeOnly: boolean;
}

/**
 * Payment Integrations Helper Service
 * Provides display names, field configurations, and gateway-specific settings
 * Based on old QMC payment-integrations.helper.ts
 */
@Injectable({
  providedIn: "root",
})
export class PaymentIntegrationsHelper {
  /**
   * Display names for payment gateway types
   * Maps internal type names to user-friendly display names
   */
  private typeDisplayNames: Record<string, string> = {
    Stripe: "Stripe",
    AuthorizeNet: "Authorize.Net",
    PayPal: "PayPal",
    Braintree: "Braintree",
    CyberSourceV2: "CyberSource v2",
    Nmi: "NMI",
    PayaV1: "Paya v1",
    TrustCommerce: "TrustCommerce",
    Square: "Square",
    Sage: "SagePay/Opayo",
    Checkout: "Checkout.com",
    PaypalV2: "PayPal v2",
    PayPalV3: "PayPal v3",
    Airwallex: "Airwallex",
    Shopify: "Shopify",
  };

  /**
   * Gateway payment type mappings
   * Stripe and PayPal support multiple payment types within their gateways
   * Matches old QMC payment-integrations.helper.ts lines 27-74
   */
  private stripePaymentTypes: GatewayPaymentType[] = [
    { code: "0", name: "Automated Clearing Settlement System" },
    { code: "1", name: "Afterpay / Clearpay" },
    { code: "2", name: "Alipay" },
    { code: "3", name: "BECS Direct Debit" },
    { code: "4", name: "Bacs Direct Debit" },
    { code: "5", name: "Bancontact" },
    { code: "6", name: "Boleto" },
    { code: "7", name: "Card" },
    { code: "8", name: "Stripe Terminal" },
    { code: "9", name: "Cash Balance" },
    { code: "10", name: "Eps" },
    { code: "11", name: "Fpx" },
    { code: "12", name: "Giropay" },
    { code: "13", name: "Grabpay" },
    { code: "14", name: "Ideal" },
    { code: "15", name: "Interac debit cards" },
    { code: "16", name: "Klarna" },
    { code: "17", name: "Konbini" },
    { code: "18", name: "Oxxo" },
    { code: "19", name: "Przelewy24" },
    { code: "20", name: "Paynow" },
    { code: "21", name: "SEPA Direct Debit" },
    { code: "22", name: "Sofort" },
    { code: "23", name: "ACH Direct Debit" },
    { code: "24", name: "WeChat Pay" },
    { code: "32", name: "Link" },
  ];

  private paypalPaymentTypes: GatewayPaymentType[] = [
    { code: "25", name: "paypal_FUNDING_PAYPAL" },
    { code: "26", name: "paypal_FUNDING_CARD" },
    { code: "27", name: "paypal_FUNDING_PAYLATER" },
    { code: "28", name: "paypal_FUNDING_CREDIT" },
    { code: "29", name: "paypal_FUNDING_VENMO" },
  ];

  /**
   * Payment integration configurations for all gateways
   * Defines field names and capabilities for each gateway type
   * Based on old QMC payment-integrations.helper.ts lines 22-163
   */
  private paymentIntegrationsConfigs: Record<string, PaymentIntegrationConfig> =
    {
      Stripe: {
        ApiAccount: null,
        ApiKey1: "Secret Key",
        ApiKey2: "Publishable Key",
        GatewayPaymentType: this.stripePaymentTypes,
        TestMode: true,
        AuthorizeOnly: false,
      },
      AuthorizeNet: {
        ApiAccount: "API Login ID",
        ApiKey1: "Transaction Key",
        TestMode: true,
        AuthorizeOnly: false,
      },
      PayPal: {
        ApiAccount: "User",
        ApiKey1: "Password",
        ApiKey2: "Signature",
        GatewayPaymentType: this.paypalPaymentTypes,
        TestMode: true,
        AuthorizeOnly: false,
      },
      Braintree: {
        ApiAccount: "Merchant ID",
        ApiKey1: "Public Key",
        ApiKey2: "Private Key",
        ApiKey3: "Merchant Account ID",
        TestMode: true,
        AuthorizeOnly: false,
      },
      CyberSourceV2: {
        ApiAccount: "Merchant ID",
        ApiKey1: "Key Detail",
        ApiKey2: "Shared Secret Key",
        TestMode: true,
        AuthorizeOnly: true,
      },
      Nmi: {
        ApiAccount: "Username",
        ApiKey1: "Password",
        TestMode: false,
        AuthorizeOnly: false,
      },
      PayaV1: {
        ApiAccount: "Merchant ID",
        ApiKey1: "Merchant key",
        TestMode: true,
        AuthorizeOnly: true,
      },
      PaypalV2: {
        ApiAccount: "PayPal Merchant ID",
        ApiKey1: "Client ID",
        ApiKey2: "Secret",
        TestMode: true,
        AuthorizeOnly: true,
      },
      PayPalV3: {
        ApiAccount: "PayPal Merchant ID",
        ApiKey1: "Client ID",
        ApiKey2: "Secret",
        TestMode: true,
        AuthorizeOnly: true,
      },
      TrustCommerce: {
        ApiAccount: "Customer ID",
        ApiKey1: "Password",
        TestMode: false,
        AuthorizeOnly: true,
      },
      Square: {
        ApiAccount: null,
        ApiKey1: "Access Token",
        ApiKey2: "Location ID",
        TestMode: true,
        AuthorizeOnly: false,
      },
      Sage: {
        ApiAccount: null,
        ApiKey1: "Integration Key",
        ApiKey2: "Integration Password",
        TestMode: true,
        AuthorizeOnly: false,
      },
      Checkout: {
        ApiAccount: null,
        ApiKey1: "Public Key",
        ApiKey2: "Secret Key",
        TestMode: true,
        AuthorizeOnly: true,
      },
      Other: {
        TestMode: false,
        AuthorizeOnly: false,
      },
      Shopify: {
        ApiAccount: "Shopify Merchant ID",
        ApiKey1: "Public Key",
        ApiKey2: "Integration Password",
        TestMode: true,
        AuthorizeOnly: false,
      },
      Airwallex: {
        ApiAccount: "Client ID",
        ApiKey1: "API Key",
        TestMode: true,
        AuthorizeOnly: false,
      },
    };

  /**
   * Get all payment method type names
   * Matches old QMC payment-integrations.helper.ts:165-167
   * @returns Array of payment method type names
   */
  getPaymentMethods(): string[] {
    return Object.keys(this.paymentIntegrationsConfigs);
  }

  /**
   * Get display name for a payment method type
   * Matches old QMC payment-integrations.helper.ts:169-174
   * @param paymentMethodType Payment method type string
   * @returns Display name or null if not found
   */
  getTypeDisplayName(paymentMethodType: string): string | null {
    return this.typeDisplayNames[paymentMethodType] || null;
  }

  /**
   * Get display name for a payment method type by enum
   * @param type Payment method type enum
   * @returns Display name or null if not found
   */
  getTypeDisplayNameByEnum(type: QPilotPaymentMethodType): string | null {
    const typeName = QPilotPaymentMethodType[type];
    return this.getTypeDisplayName(typeName);
  }

  /**
   * Get field display name (e.g., "Secret Key" for ApiKey1 on Stripe)
   * Matches old QMC payment-integrations.helper.ts:176-183
   * @param paymentMethodType Payment method type string
   * @param fieldName Field name (ApiAccount, ApiKey1, etc.)
   * @returns Field display name or null if not found
   */
  getFieldDisplayName(
    paymentMethodType: string,
    fieldName: string,
  ): string | null {
    const config = this.paymentIntegrationsConfigs[paymentMethodType];
    if (config && config[fieldName as keyof PaymentIntegrationConfig]) {
      return config[fieldName as keyof PaymentIntegrationConfig] as string;
    }
    return null;
  }

  /**
   * Check if gateway supports test mode
   * Matches old QMC payment-integrations.helper.ts:185-189
   * @param paymentMethodType Payment method type string
   * @returns True if test mode is supported
   */
  canEnableTestMode(paymentMethodType: string): boolean {
    if (!paymentMethodType) return false;
    const config = this.paymentIntegrationsConfigs[paymentMethodType];
    return config?.TestMode || false;
  }

  /**
   * Check if gateway supports authorize-only mode
   * Matches old QMC payment-integrations.helper.ts:191-195
   * @param paymentMethodType Payment method type string
   * @returns True if authorize-only mode is supported
   */
  canAuthorizeOnly(paymentMethodType: string): boolean {
    if (!paymentMethodType) return false;
    const config = this.paymentIntegrationsConfigs[paymentMethodType];
    return config?.AuthorizeOnly || false;
  }

  /**
   * Get gateway payment types for Stripe or PayPal
   * @param paymentMethodType Payment method type string
   * @returns Array of gateway payment types or undefined
   */
  getGatewayPaymentTypes(
    paymentMethodType: string,
  ): GatewayPaymentType[] | undefined {
    const config = this.paymentIntegrationsConfigs[paymentMethodType];
    return config?.GatewayPaymentType;
  }

  /**
   * Get gateway payment type name by code
   * @param paymentMethodType Payment method type string
   * @param code Payment type code
   * @returns Payment type name or null
   */
  getGatewayPaymentTypeName(
    paymentMethodType: string,
    code: string,
  ): string | null {
    const types = this.getGatewayPaymentTypes(paymentMethodType);
    if (!types) return null;
    const found = types.find((t) => t.code === code);
    return found?.name || null;
  }

  /**
   * Get all Stripe payment types
   * @returns Array of Stripe payment types
   */
  getStripePaymentTypes(): GatewayPaymentType[] {
    return this.stripePaymentTypes;
  }

  /**
   * Get all PayPal payment types
   * @returns Array of PayPal payment types
   */
  getPayPalPaymentTypes(): GatewayPaymentType[] {
    return this.paypalPaymentTypes;
  }

  /**
   * Get configuration for a payment method type
   * @param paymentMethodType Payment method type string
   * @returns Payment integration config or undefined
   */
  getConfig(paymentMethodType: string): PaymentIntegrationConfig | undefined {
    return this.paymentIntegrationsConfigs[paymentMethodType];
  }
}
